<template>
  <div class="share-page container py-4">
    <h2 class="fw-bold mb-3 text-primary text-center">
      {{ $t('shares.title') }}
    </h2>
    <p class="text-muted text-center mb-3" v-html="$t('shares.body')"></p>


    <!-- 頁籤 -->
    <div class="share-tabs mb-4">
      <button
        type="button"
        class="tab-btn"
        :class="{ active: activeTab === 'instagram' }"
        @click="setTab('instagram')"
      >
        Instagram
      </button>
      <button
        type="button"
        class="tab-btn"
        :class="{ active: activeTab === 'youtube' }"
        @click="setTab('youtube')"
      >
        YouTube
      </button>

      <!-- 🌊 滑動的膠囊背景 -->
      <span
        class="tab-glider"
        :class="{
          'pos-instagram': activeTab === 'instagram',
          'pos-youtube': activeTab === 'youtube'
        }"
      ></span>
    </div>


    <!-- 錯誤 -->
    <div v-if="errorMsg" class="alert alert-danger">
      {{ errorMsg }}
    </div>

    <!-- 載入中 -->
    <div v-else-if="loading" class="text-center py-5">
      內容載入中…
    </div>

    <!-- 有任何資料 -->
    <div v-else-if="shares.length">
      <!-- 該平台有資料 -->
      <div v-if="filteredShares.length" class="shares-grid">
        <div
          v-for="item in visibleShares"
          :key="item.id"
          class="share-card"
        >
          <h5 class="fw-bold mb-1">
            <a
              class="share-title-link text-primary"
              :href="item.url"
              target="_blank"
              rel="noopener"
              :aria-label="item.title"
              @click.stop
            >
              {{ item.title }}
            </a>
          </h5>

          <p v-if="item.sub_title" class="text-muted small mb-2">
            {{ item.sub_title }}
          </p>

          <!-- Instagram 內嵌 -->
          <div
            v-if="isInstagram(item.url)"
            class="embed-box ig-embed"
          >
            <blockquote
              class="instagram-media"
              data-instgrm-captioned
              :data-instgrm-permalink="cleanInstagramUrl(item.url)"
              data-instgrm-version="14"
            ></blockquote>
          </div>

          <!-- YouTube 內嵌 -->
          <div
            v-else-if="isYoutube(item.url)"
            class="embed-box youtube-box"
          >
            <iframe
              :src="buildYoutubeEmbed(item.url)"
              frameborder="0"
              allowfullscreen
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            ></iframe>
          </div>

          <!-- 其他平台：先顯示為外連 -->
          <div v-else class="mt-2">
            <a :href="item.url" target="_blank" rel="noopener">
              前往觀看：{{ item.url }}
            </a>
          </div>
        </div>
      </div>

      <!-- 無該平台資料 -->
      <div v-else class="text-center text-muted py-5">
        目前此平台尚未建立任何醫師分享內容。
      </div>

      <!-- 底部狀態提示（只在該平台有資料時顯示） -->
      <div v-if="filteredShares.length" class="mt-3 text-center text-muted small">
        <span v-if="isLoadingMore">
          載入更多內容中…
        </span>
        <span v-else-if="visibleShares.length < filteredShares.length">
          向下捲動以載入更多內容…
        </span>
        <span v-else>
          已顯示全部內容
        </span>
      </div>
    </div>

    <!-- 完全沒有任何資料 -->
    <div v-else class="text-center text-muted py-5">
      尚未建立任何醫師分享內容。
    </div>
  </div>
</template>

<script>
import { supabase } from '@/lib/supabase'

export default {
  name: 'Share',
  data () {
    const INITIAL_COUNT = 9 // 3 排 * 3 個 = 9
    return {
      loading: true,
      errorMsg: '',
      shares: [],
      activeTab: 'instagram',

      itemsToShow: INITIAL_COUNT,
      loadStep: INITIAL_COUNT,
      isLoadingMore: false
    }
  },

  async mounted () {
    await this.loadShares()
    window.addEventListener('scroll', this.handleScroll, { passive: true })
  },

  beforeUnmount () {
    window.removeEventListener('scroll', this.handleScroll)
  },

  computed: {
    // 依目前頁籤過濾
    filteredShares () {
      if (this.activeTab === 'instagram') {
        return this.shares.filter(s => this.isInstagram(s.url))
      }
      if (this.activeTab === 'youtube') {
        return this.shares.filter(s => this.isYoutube(s.url))
      }
      return this.shares
    },

    // 實際渲染在畫面上的幾筆
    visibleShares () {
      return this.filteredShares.slice(0, this.itemsToShow)
    }
  },

  watch: {
    // 一旦 shares 更新，等 DOM 更新完再處理 IG 內嵌
    shares () {
      this.$nextTick(() => {
        this.processInstagramEmbeds()
      })
    },
    // 切換頁籤也重置顯示數量 + 重新處理 IG
    activeTab () {
      this.resetPagination()
      this.$nextTick(() => {
        this.processInstagramEmbeds()
      })
    }
  },

  methods: {
    async loadShares () {
      this.loading = true
      this.errorMsg = ''

      try {
        const { data, error } = await supabase
          .from('shares')
          .select('id, title, sub_title, platform, url, created_at')
          .order('created_at', { ascending: false })

        console.log('[Share] data=', data, 'error=', error)

        if (error) throw error
        this.shares = data || []
        this.resetPagination()
      } catch (e) {
        console.error('[Share] loadShares error', e)
        this.errorMsg = '醫師分享內容載入失敗，請稍後再試。'
      } finally {
        this.loading = false
      }
    },

    resetPagination () {
      this.itemsToShow = this.loadStep
      this.isLoadingMore = false
    },

    setTab (tab) {
      if (this.activeTab !== tab) {
        this.activeTab = tab
      }
    },

    // ==== 平台判斷 ====
    isInstagram (url) {
      return /instagram\.com/.test(url || '')
    },
    isYoutube (url) {
      return /youtu\.be|youtube\.com/.test(url || '')
    },

    // 把 IG 分享連結清成「沒有 query 的 canonical 形式」
    cleanInstagramUrl (raw) {
      try {
        const u = new URL(raw)
        return `${u.origin}${u.pathname}` // 保留 /reel/xxx/ 那段
      } catch (e) {
        return raw
      }
    },

    // YouTube 轉成 embed 用網址
    buildYoutubeEmbed (raw) {
      if (!this.isYoutube(raw)) return null

      let videoId = null

      try {
        // 1) youtu.be/xxxxxxx
        if (raw.includes('youtu.be/')) {
          videoId = raw.split('youtu.be/')[1].split(/[?&]/)[0]
        }
        // 2) youtube.com/watch?v=xxxxxx
        else if (raw.includes('watch?v=')) {
          const u = new URL(raw)
          videoId = u.searchParams.get('v')
        }
        // 3) youtube.com/shorts/xxxxxx
        else if (raw.includes('/shorts/')) {
          videoId = raw.split('/shorts/')[1].split(/[?&]/)[0]
        }
        // 4) 已經是 /embed/xxxxxx 的就直接吃
        else if (raw.includes('/embed/')) {
          videoId = raw.split('/embed/')[1].split(/[?&]/)[0]
        }
      } catch (e) {
        console.debug('[Share] parse youtube url failed', e)
      }

      if (!videoId) return null

      return `https://www.youtube.com/embed/${videoId}`
    },

    // ==== 無限捲動 ====
    handleScroll () {
      if (this.loading || this.errorMsg) return
      if (!this.filteredShares.length) return
      if (this.isLoadingMore) return

      const scrollBottom = window.innerHeight + window.scrollY
      const docHeight = document.documentElement.scrollHeight || document.documentElement.offsetHeight

      // 距離底部 80px 以內就視為要載入更多
      if (docHeight - scrollBottom < 80) {
        this.tryLoadMore()
      }
    },

    tryLoadMore () {
      const total = this.filteredShares.length
      if (this.itemsToShow >= total) return

      this.isLoadingMore = true
      this.itemsToShow = Math.min(this.itemsToShow + this.loadStep, total)
      this.isLoadingMore = false

      // 新增的卡片如果是 IG，要再 process 一次
      this.$nextTick(() => {
        this.processInstagramEmbeds()
      })
    },

    // ==== IG 內嵌處理 ====
    processInstagramEmbeds () {
      // 沒有 IG 分享就不用載 script
      const hasIg = this.filteredShares.some(s => this.isInstagram(s.url))
      if (!hasIg) return

      const runProcess = () => {
        try {
          if (window.instgrm && window.instgrm.Embeds && typeof window.instgrm.Embeds.process === 'function') {
            window.instgrm.Embeds.process()
          }
        } catch (e) {
          console.debug('[Share] instgrm process failed', e)
        }
      }

      // 第一次：還沒有 script → 動態載入，onload 再跑 process
      if (!window.instgrm) {
        const existing = document.getElementById('instagram-embed-script')
        if (existing) {
          existing.onload = () => runProcess()
          return
        }

        const s = document.createElement('script')
        s.id = 'instagram-embed-script'
        s.src = 'https://www.instagram.com/embed.js'
        s.async = true
        s.onload = () => runProcess()
        document.body.appendChild(s)
      } else {
        // script 已經載完：直接 process 就好
        runProcess()
      }
    }
  }
}
</script>

<style scoped>
.share-page {
  max-width: 1200px;
}

/* ================================
   頁籤 Tab 容器 + 動畫 glider
================================ */
.share-tabs {
  position: relative;
  display: inline-flex;
  justify-content: center;
  align-items: center;
  gap: 0;                /* 由 glider 來做區隔 */
  padding: 4px;
  border-radius: 999px;
  background: #e7f1ff;
  margin: 0 auto 24px;
}

/* 兩個按鈕本身 */
.tab-btn {
  position: relative;
  z-index: 2;            /* 壓在 glider 上面 */
  flex: 1 1 auto;
  min-width: 120px;
  padding: 8px 18px;
  border-radius: 999px;
  border: 0;
  background: transparent;
  color: #0b69b7;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: color 0.2s ease, transform 0.15s ease;
  white-space: nowrap;
}

.tab-btn:hover {
  transform: translateY(-1px);
}

.tab-btn.active {
  color: #0d6efd;
}

/* 滑動的膠囊背景 */
.tab-glider {
  position: absolute;
  top: 4px;
  bottom: 4px;
  left: 4px;
  width: calc(50% - 4px);      /* 兩個 tab，glider 佔一半寬 */
  border-radius: 999px;
  background: #ffffff;
  box-shadow: 0 4px 18px rgba(0, 0, 0, 0.12);
  transition: transform 0.25s ease;
  z-index: 1;
}

/* 根據 activeTab 把 glider 滑到左/右 */
.tab-glider.pos-instagram {
  transform: translateX(0%);
}

.tab-glider.pos-youtube {
  transform: translateX(100%);
}

/* 手機縮小時微調一下尺寸 */
@media (max-width: 640px) {
  .share-tabs {
    padding: 3px;
  }

  .tab-btn {
    min-width: 0;
    padding: 6px 14px;
    font-size: 13px;
  }

  .tab-glider {
    top: 3px;
    bottom: 3px;
    left: 3px;
    width: calc(50% - 3px);
  }
}

/* ================================
   卡片排版：桌機 3 欄
================================ */
.shares-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr)); /* 固定 3 欄 */
  gap: 24px;
}

/* 卡片外觀 */
.share-card {
  background: #fff;
  border-radius: 20px;
  padding: 18px 20px 24px;
  box-shadow: 0 2px 10px rgba(0,0,0,0.05);
  transition: box-shadow 0.15s ease;
}

.share-card:hover {
  box-shadow: 0 4px 16px rgba(0,0,0,0.08);
}

/* ================================
   Instagram：直式卡片
================================ */
.ig-embed {
  margin-top: 10px;
}

.ig-embed :deep(.instagram-media) {
  max-width: 100% !important;
  border-radius: 16px !important;
  margin: 0 auto !important;
  overflow: hidden !important;
}


/* ================================
   YouTube：短影片（直式）
   ※ 如果有判斷 short，就給這個 class
================================ */
.yt-vertical {
  position: relative;
  width: 100%;
  padding-bottom: 180%; /* 9:16 */
  height: 0;
  margin-top: 10px;
  overflow: hidden;
}

.yt-vertical iframe {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  border: 0;
}

/* ================================
   YouTube：一般影片（橫式）
================================ */
.yt-horizontal {
  position: relative;
  width: 100%;
  padding-bottom: 56.25%; /* 16:9 */
  height: 0;
  margin-top: 10px;
  overflow: hidden;
}

.yt-horizontal iframe {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  border: 0;
}

/* ================================
   breakpoint：寬度略縮時 → 2 欄
   （視你整個 layout，可在 1100 / 1024 左右調整）
================================ */
@media (max-width: 1500px) {
  .shares-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr)); /* 變成 2 欄 */
  }
}

/* ================================
   手機：1 欄
================================ */
@media (max-width: 1100px) {
  .shares-grid {
    grid-template-columns: 1fr;
  }

  .tab-btn {
    padding: 6px 14px;
    font-size: 13px;
  }
}

.share-title-link {
  text-decoration: none;
}

.share-title-link:hover {
  text-decoration: underline;
}

</style>
